import axios from 'axios';
import { API_BASE_URL } from '@/lib/config';
import { getShareAccessToken } from './share-access-storage';

/**
 * Dedicated, unauthenticated HTTP client for the public Shared Startup
 * Profile flow (MYN-58). Deliberately NOT the shared `apiClient` - this path
 * has no platform session, so it must never attach the platform's bearer
 * token or react to a 401 by clearing it (`api/client.ts`'s interceptors).
 * Attaches the share view-session token (see `share-access-storage.ts`) only
 * on the one request that needs it.
 */
const sharedProfileClient = axios.create({ baseURL: API_BASE_URL });

export type SharedProfileLinkStatus = 'active' | 'expired' | 'revoked' | 'pending';

export interface SharedProfileStatusResponse {
  status: SharedProfileLinkStatus;
  recipientEmail: string;
  startupCompanyName: string;
}

/** A sector/subsector pairing on the public profile (resolved display names). */
export interface PublicStartupSector {
  sector: string;
  subsector: string | null;
  isPrimary: boolean;
}

/** A founding-team member on the public profile. */
export interface PublicFoundingTeamMember {
  name: string;
  title: string;
  linkedinUrl: string | null;
  pastExits: boolean;
  // No `photoUrl`: the founding-team member photo column is dropped
  // Backend-side (MYN-175, A-49). `bio` may still be returned until the
  // Backend drops it, but it was removed from the product (UX sheet r127):
  // never render it.
  bio?: string | null;
}

/**
 * Redacted, public-safe Startup Profile (MYN-41/58) - mirrors the backend
 * `PublicStartupProfile` DTO. Enum tokens are raw (humanized here for
 * display); geography/sector are resolved names; money fields are numbers.
 */
export interface PublicStartupProfile {
  companyName: string;
  briefDescription: string;
  aiClassification: string | null;
  fundingStage: string | null;
  amountRaisedToDate: number | null;
  nondilutiveFunding: number | null;
  recentMilestone: string | null;
  yearFounded: number | null;
  hqCity: string | null;
  hqState: string | null;
  hqCountry: string | null;
  companyWebsiteUrl: string | null;
  companyLinkedinUrl: string | null;
  logoUrl: string | null;
  totalRevenueRange: string | null;
  revenueModel: string | null;
  revenueGrowthRate: string | null;
  payingCustomers: string | null;
  arr: number | null;
  financeSoughtLow: number | null;
  financeSoughtHigh: number | null;
  instrument: string | null;
  targetCloseDate: string | null;
  valuationOrCap: number | null;
  hasLeadInvestor: boolean;
  leadInvestorName: string | null;
  founderFemale: boolean;
  founderUnderrepresented: boolean;
  founderPreviousExit: boolean;
  /**
   * Presence only. The backend deliberately withholds the stored S3 key from
   * this payload - a share recipient is external, and the key embeds an
   * internal user id (MYN-101).
   */
  hasPitchDeck: boolean;
  pitchDeckFileMimeType: string | null;
  /** Presigned GET, safe to put in an href. Null when signing failed (MYN-101). */
  pitchDeckDownloadUrl: string | null;
  /** External link the founder supplied (A-58). Never presigned. */
  pitchVideoUrl: string | null;
  useOfFunds: string | null;
  employeeCount: number | null;
  sectors: PublicStartupSector[];
  marketModels: string[];
  investorTypes: string[];
  foundingTeam: PublicFoundingTeamMember[];
}

const base = (token: string) => `/public/shares/${token}`;

/** Check a share link's status (MYN-58 link-expired vs. email-verify gate). */
export async function getSharedProfileStatus(token: string): Promise<SharedProfileStatusResponse> {
  const { data } = await sharedProfileClient.get<SharedProfileStatusResponse>(`${base(token)}/status`);
  return data;
}

/** Send (or resend) the recipient's view-verification code. */
export async function sendSharedProfileOtp(token: string): Promise<void> {
  await sharedProfileClient.post(`${base(token)}/send-otp`);
}

/** Verify the code and receive a short-lived view-session access token. */
export async function verifySharedProfileOtp(
  token: string,
  code: string,
): Promise<{ accessToken: string }> {
  const { data } = await sharedProfileClient.post<{ accessToken: string }>(
    `${base(token)}/verify-otp`,
    { code },
  );
  return data;
}

/**
 * Fetch the read-only Startup Profile for a verified recipient. Attaches the
 * stored view-session token; the backend re-validates status/expiry/
 * revocation and the token itself on every call (MYN-54 "validate on every
 * access").
 */
export async function getSharedStartupProfile(token: string): Promise<PublicStartupProfile> {
  const accessToken = getShareAccessToken(token);
  const { data } = await sharedProfileClient.get<PublicStartupProfile>(`${base(token)}/profile`, {
    headers: accessToken ? { 'X-Share-Access-Token': accessToken } : undefined,
  });
  return data;
}
